import { getUserSignedInState } from "@brandclub/common-ui";
import FlowContainer from "@/components/pages/AppPages/Pages/components/FlowContainer";
import {
  OutlineButton,
  SolidButton,
} from "@/components/StoreComponents/StoreButton";
import { StoreFinish } from "@/components/StoreComponents/StoreFinish";
import { useTrackActions } from "@/utils/hooks/useTracking";
import { AnalyticsTrackingEvent } from "@brandclub/common-ui";
import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

interface OrderConfirmationProps {
  orderNumber?: string;
  customerName?: string;
  shippingAddress?: string;
  estimatedDeliveryDate?: Date;
  savedAmount?: number;
  savedAmountReason?: string;
  productImageUrl?: string;
}

type UserLoginStatus = "notChecked" | "loggedIn" | "notLoggedIn";

export const OrderSuccess: React.FC<OrderConfirmationProps> = () => {
  const [searchParams] = useSearchParams();
  const [trackAction] = useTrackActions();
  const orderId = searchParams.get("orderId");
  const [userLoginStatus, setUserLoginStatus] =
    useState<UserLoginStatus>("notChecked");

  // Navigation function to go to checkout summary
  const navigate = useNavigate();
  const handleTrackOrderClick = () => {
    trackAction?.(
      AnalyticsTrackingEvent.CHECKOUT_CONNECT_SUCCESS_TRACK_ORDER_CLICK,
      {
        orderId,
      }
    );
    navigate(`/checkoutSummary?orderId=${orderId}`);
  };

  const handleContinueShoppingClick = () => {
    trackAction?.(
      AnalyticsTrackingEvent.CHECKOUT_CONNECT_SUCCESS_CONTINUE_SHOPPING_CLICK,
      {
        orderId,
      }
    );
    navigate("/");
  };

  useEffect(() => {
    if (orderId) {
      trackAction?.(
        AnalyticsTrackingEvent.CHECKOUT_CONNECT_SUCCESS_SCREEN_VISIT,
        {
          orderId,
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    const checkUserInfo = async () => {
      try {
        const { userInfo, signedIn } = await getUserSignedInState();
        if (userInfo && signedIn) {
          setUserLoginStatus("loggedIn");
          return;
        }
      } catch (error) {
        console.error(error);
      }
      // this route requires login, so redirect to login page
      setUserLoginStatus("notLoggedIn");
      navigate("/signin", {
        replace: true,
        state: {
          redirectPath: {
            pathname: "/checkout/thankyou",
            search: searchParams.toString(),
          },
        },
      });
    };

    checkUserInfo();
  }, [navigate, searchParams]);

  useEffect(() => {
    if (orderId === null && userLoginStatus === "loggedIn") {
      console.warn("no order id found in the URL; navigating to purchase");
      navigate("/dashboard/purchase");
    }
  }, [orderId, navigate, userLoginStatus]);

  const renderCallToActions = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        gap={"16px"}
        sx={{ width: "100%" }}
      >
        <SolidButton
          onClick={handleTrackOrderClick}
          disableMaxWidth
          sx={{ width: "100%", maxWidth: 380 }}
        >
          Track my order
        </SolidButton>
        <OutlineButton
          onClick={handleContinueShoppingClick}
          disableMaxWidth
          sx={{ width: "100%", maxWidth: 380 }}
        >
          Continue shopping
        </OutlineButton>
      </Box>
    );
  };
  // Desktop version
  return (
    <FlowContainer>
      <StoreFinish
        overlineText={`Order #${orderId}`}
        title="Your order is on the way!"
        subtitle={
          "We’re placing your order with Amazon — we’ll send you a text message if we need more information to complete your order."
        }
        sx={{
          width: "100%",
          position: "relative",
          zIndex: 1,
          maxWidth: 615,
        }}
      >
        {renderCallToActions()}
      </StoreFinish>
    </FlowContainer>
  );
};

export default OrderSuccess;
